import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/Settings.css";
import "../styles/global.css";
import Header from "../components/Header";
import { useDarkMode } from "../context/DarkModeContext"; // 👈 Import the context
import { Cog } from "lucide-react";
import { useLocation } from "react-router-dom"; // Import for detecting route changes

const Settings = () => {
  const { darkMode, toggleDarkMode } = useDarkMode(); // 👈 Use the context here
  const [isLoaded, setIsLoaded] = useState(false); // Animation state
  
  // Get current location to detect route changes
  const location = useLocation();
  
  // Reset and replay animations on component mount and when route changes
  useEffect(() => {
    // Reset animation state first
    setIsLoaded(false);
    
    // Small delay to ensure reset takes effect before starting animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [location.pathname]); // Re-run effect when the path changes

  // ✅ Apply Dark Mode on initial load
  useEffect(() => {
    document.body.classList.add('bg-lightmode', 'bg-cover', 'bg-center', 'min-h-screen');

    if (darkMode) {
      document.documentElement.classList.add("dark");
      document.body.classList.add("bg-darkmode");
      document.body.classList.remove("bg-lightmode");
    } else {
      document.documentElement.classList.remove("dark");
      document.body.classList.remove("bg-darkmode");
      document.body.classList.add("bg-lightmode");
    }
  }, [darkMode]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden p-4">
      <div className={`flex-grow overflow-y-auto transition-all duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <h2 className={`text-2xl text-gray-800 font-semibold mb-4 dark:text-white flex transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Cog className="mr-2" />Settings
        </h2>
        <div className={`flex items-center gap-3 mb-4 transition-all duration-500 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <label className="relative inline-flex items-center cursor-pointer z-10 ml-4">
            <input
              type="checkbox"
              checked={darkMode}
              onChange={toggleDarkMode}
              className="sr-only peer"
            />
            <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uasYellow`}></div>
          </label>
          <span className="text-gray-700 dark:text-gray-200">Enable Dark Mode</span>
        </div>
        
        {/* Additional settings sections can be added here with increasing delays
        <div className={`bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow p-6 mb-4 transition-all duration-500 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Account Preferences</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Notifications</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receive email notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uasYellow"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Auto-save</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Automatically save changes</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-uasYellow"></div>
              </label>
            </div>
          </div>
        </div>
        
        <div className={`bg-white dark:bg-laryzeDarkGray dark:bg-opacity-90 rounded-lg shadow p-6 transition-all duration-500 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">Display Settings</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="fontsize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Font Size</label>
              <select id="fontsize" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white">
                <option value="small">Small</option>
                <option value="medium" selected>Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
              <select id="language" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white">
                <option value="en" selected>English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>
          </div>
        </div>
        */}
      </div>
    </div>
  );
};

export default Settings;